import { useCallback } from 'react';

import { UnreachableError } from '@/utils/errors';

import { useAutoPositionAction } from '../actions/autoPosition.action';
import { closeSidePanelAction } from '../actions/closeSidePanel.action';
import { useCompleteInitializationAction } from '../actions/completeInitialization.action';
import { endExportAction } from '../actions/endExport.action';
import { useFamilyTreeDataLoadedAction } from '../actions/familyTreeDataLoaded.action';
import { useMarriageLayoutComputedAction } from '../actions/marriageLayoutComputed.action';
import { useNodesChangedAction } from '../actions/nodesChanged.action';
import { openSidePanelAction } from '../actions/openSidePanel.action';
import { setRenderWhiteLabeledAction } from '../actions/setRenderWhiteLabeled.action';
import { startExportAction } from '../actions/startExport.action';
import { useUpdateNodeConfigAction } from '../actions/updateNodeConfig.action';
import { useUpdateVisualizationConfigAction } from '../actions/updateVisualizationConfig.action';
import { FamilyTreeReducer } from '../types';

export function useFamilyTreeReducer(): FamilyTreeReducer {
  const autoPositionAction = useAutoPositionAction();
  const completeInitializationAction = useCompleteInitializationAction();
  const familyTreeDataLoadedAction = useFamilyTreeDataLoadedAction();
  const nodesChangedAction = useNodesChangedAction();
  const marriageLayoutComputedAction = useMarriageLayoutComputedAction();
  const updateVisualizationConfigAction = useUpdateVisualizationConfigAction();
  const updateNodeConfigAction = useUpdateNodeConfigAction();

  return useCallback<FamilyTreeReducer>(
    (state, action, queueEffect) => {
      switch (action.type) {
        case 'AUTO_POSITION':
          return autoPositionAction(state, action, queueEffect);
        case 'COMPLETE_INITIALIZATION':
          return completeInitializationAction(state, action, queueEffect);
        case 'FAMILY_TREE_DATA_LOADED':
          return familyTreeDataLoadedAction(state, action, queueEffect);
        case 'NODES_CHANGED':
          return nodesChangedAction(state, action, queueEffect);
        case 'MARRIAGE_LAYOUT_COMPUTED':
          return marriageLayoutComputedAction(state, action, queueEffect);
        case 'MARRIAGE_LAYOUT_FAILED':
          return state; // Return the current state if the marriage layout fails
        case 'OPEN_SIDE_PANEL':
          return openSidePanelAction(state, action, queueEffect);
        case 'CLOSE_SIDE_PANEL':
          return closeSidePanelAction(state, action, queueEffect);
        case 'UPDATE_VISUALIZATION_CONFIG':
          return updateVisualizationConfigAction(state, action, queueEffect);
        case 'UPDATE_NODE_CONFIG':
          return updateNodeConfigAction(state, action, queueEffect);
        case 'START_EXPORT':
          return startExportAction(state, action, queueEffect);
        case 'END_EXPORT':
          return endExportAction(state, action, queueEffect);
        case 'SET_RENDER_WHITE_LABELED':
          return setRenderWhiteLabeledAction(state, action, queueEffect);
        default:
          throw new UnreachableError({
            case: action,
            message: `Unknown action ${action} `,
          });
      }
    },
    [
      autoPositionAction,
      completeInitializationAction,
      familyTreeDataLoadedAction,
      nodesChangedAction,
      marriageLayoutComputedAction,
      updateVisualizationConfigAction,
      updateNodeConfigAction,
    ]
  );
}
